<template>
  <div class="layout-config">
    <div class="layout-config-content-wrapper">
      <a class="layout-config-button layout-config-button-righ" style="top: 69%" @click="showEvent = !showEvent">
        <i class="pi pi-external-link" v-tooltip="'События'"></i>
      </a>
    </div>
  </div>
  <div v-show="showEvent">
    <TabView>
      <TabPanel header="Активные события">
        <ActiveEvent scrollHeight="255px" />
      </TabPanel>
      <TabPanel header="События">
        <ViewCustomData v-if="eventTypeData && startDate" entityName="event" entitySpec="data_web"
          serviceName="BaseService" :dictionary="DICTIONARY_EVENT" :columns="EVENT_COLUMNS" ref="viewEventData"
          :rows="100" scrollHeight="220px" :toolbarFilters="TOOLBAR_FILTERS" sortField="event_date"
          :drawRow="rowClassEvent" :prepareData="prepareData" :intervalTimer="$store.getters.eventUpdate * 1000"
          :visibleButtons="{ insert: false, edit: false, delete: false }">
          <template #toolbar>
            <div class="component-filter">
              <Button title="Свернуть" icon="pi pi-minus" class="m-1" @click="showEvent = false" label="Свернуть" />
              <Button title="Фильтр" icon="pi pi-filter" class="m-1" @click="showFilter = true" label="Фильтр"
                :disabled="disableFilter" />
            </div>
          </template>
        </ViewCustomData>
        <Dialog v-model:visible="showFilter" :style="{ width: '40%' }" modal @hide="filterApplay">
          <template #header>
            <i class="pi pi-filter mr-3" style="font-size: 1.5rem; color: rgb(204,204,0)" />
            <div class="col-10 font-bold text-center" style="font-size: 18px">
              Настройки отображения типов событий
            </div>
          </template>
          <DataTable :value="eventTypeData" selectionMode="single" dataKey="event_type_id"
            class="editable-cells-table p-datatable-sm p-datatable-gridlines" autoLayout :loading="loadingEventType"
            :rowClass="rowClassEventType">
            <template #empty> Записи не найдены. </template>
            <template #loading> Записи загружаются. Пожалуйста подождите. </template>
            <Column field="event_type_name" header="Тип события"> </Column>
            <Column field="event_group_name" header="Группа"> </Column>
            <Column field="is_visible" header="Отбражать" bodyStyle="text-align: center">
              <template #body="slotProps">
                <Checkbox v-model="slotProps.data.is_visible" binary @change="handleVisibilityChange(slotProps.data)"
                  :disabled="!$store.getters.info.isAdmin || !$store.getters.info.isControl" />
              </template>
            </Column>
          </DataTable>
          <template #footer>
            <div class="mt-4">
              <Button label="Снять все" icon="pi pi-times" class="p-button-text" @click="unselectAll" />
              <Button label="Выделить все" icon="pi pi-check-circle" class="p-button-text" @click="selectAll" />
            </div>
          </template>
        </Dialog>
      </TabPanel>
    </TabView>
  </div>
</template>

<script setup>
import { ref, onMounted, defineOptions } from 'vue';
import { useStore } from 'vuex';
import { EVENT_COLUMNS, DICTIONARY_EVENT, TOOLBAR_FILTERS } from './eventConstants';
import { EVENT_GROUP_STYLES, EVENT_TYPE_STYLES } from './eventStyles';
import ActiveEvent from '../forms/TTMEventForm/ActiveEvent.vue';

const store = useStore();

const showFilter = ref(false);
const showEvent = ref(false);
const disableFilter = ref(true);
const loadingEventType = ref(false);
const eventTypeData = ref(null);
const eventData = ref(null);
const startDate = ref(new Date());

const viewEventData = ref(null);

let eventTypeService = null;

const handleVisibilityChange = async (eventType) => {
  try {
    await eventTypeService.putData({
      event_type_id: eventType.event_type_id,
      is_visible: eventType.is_visible,
    });
  } catch (er) {
    eventType.is_visible = !eventType.is_visible;
    store.commit('SET_ERROR', {
      er: er?.response ?? er,
      source: ['EventData', 'handleVisibilityChange'],
    });
  }
};

const unselectAll = async () => {
  const invisibleEventTypes = eventTypeData.value.filter(item => item.is_visible);
  const result = [];
  loadingEventType.value = true;
  for (const eventType of invisibleEventTypes) {
    eventType.is_visible = false;
    result.push(handleVisibilityChange(eventType));
  }
  await Promise.all(result);
  loadingEventType.value = false;
};

const selectAll = async () => {
  const visibleEventTypes = eventTypeData.value.filter(item => !item.is_visible);
  const result = [];
  loadingEventType.value = true;
  for (const eventType of visibleEventTypes) {
    eventType.is_visible = true;
    result.push(handleVisibilityChange(eventType));
  }
  await Promise.all(result);
  loadingEventType.value = false;
};

const filterEvents = (data) => {
  const visibleEventTypeIds = getVisibleEventTypeIds();
  return data.filter(item => visibleEventTypeIds.includes(item.event_type_id));
};

const getVisibleEventTypeIds = () => {
  return eventTypeData.value
    .filter(item => item.is_visible)
    .map(item => item.event_type_id);
};

const rowClassEvent = (data) => {
  return EVENT_GROUP_STYLES[data.event_group_index] || EVENT_GROUP_STYLES.default;
};

const rowClassEventType = (data) => {
  return EVENT_TYPE_STYLES[data.event_group_id] || EVENT_TYPE_STYLES.default;
};

const prepareData = (data) => {
  disableFilter.value = false;
  eventData.value = [...data];
  updateStartDateIfNeeded();
  return data;
};

const updateStartDateIfNeeded = () => {
  if (isNextDay(startDate.value)) {
    startDate.value = null;
    setTimeout(() => {
      startDate.value = new Date();
    }, 3000);
  }
};

const isNextDay = (startDate) => {
  const now = new Date();
  return now.getDate() > startDate.getDate() ||
    now.getMonth() > startDate.getMonth() ||
    now.getFullYear() > startDate.getFullYear();
};

const filterApplay = () => {
  viewEventData.value.updateData(eventData.value);
  showFilter.value = false;
};

const fetchEventTypeData = () => {
  loadingEventType.value = true;
  eventTypeService
    .getData()
    .then((data) => {
      eventTypeData.value = data;
    })
    .finally(() => {
      loadingEventType.value = false;
    });
};

const initEventTypeService = () => {
  eventTypeService = store.getters.nameToService(
    'BaseService',
    null,
    null,
    'event_type_filter',
    'data'
  );
};

onMounted(() => {
  initEventTypeService();
  fetchEventTypeData();
});

</script>

<style scoped lang="scss">
::v-deep(.p-tabview .p-tabview-nav li .p-tabview-nav-link) {
  font-weight: normal;
  padding: 0.36rem;
}

::v-deep(.p-tabview-panels) {
  padding: 0.1rem;
}

::v-deep(.row-accessories-blue) {
  background-color: rgba(57, 26, 196, 1) !important;
}

::v-deep(.row-accessories-green) {
  background-color: #c0dcc0 !important;
}

::v-deep(.row-accessories-red) {
  background-color: #ff6c6c !important;
}

::v-deep(.row-accessories-yellow) {
  background-color: #fcfa98 !important;
}

::v-deep(.row-accessories-orange) {
  background-color: #f8d69c !important;
}

::v-deep(.row-accessories-red_ligt) {
  background-color: #f58484 !important;
}
</style>